import { Injectable } from '@angular/core';
import { UtilService } from '../util/util.service';
import {LegacyInitializer} from "../initialization/legacy.initializer";
import { Papa } from 'ngx-papaparse';
import {
  InternationalizationService,
  LocaleCustomization,
  LocaleKeyValue
} from "../internationalization/internationalization.service";
import { Subject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';



@Injectable({
  providedIn: 'root'
})
export class UserService {

  private authenticated: boolean;
  private afterSessionTimeOutUrl: string;
  private id: string;
  private theUserName: string;
  private firstName: string;
  private lastName: string;
  private timeZone: string;
  private language: string;
  private locales: LocaleKeyValue[];
  private languages: LocaleKeyValue[];
  private multilingual: boolean;
  private localeCustomizations: any[];
  private languageElements: string[];
  private photo: any;
  private roles: string[];
  private home: string;
  private sofiUser = {};
  private properties = {};
  private userProfile = {};
  private baseUrl: string;
  private build: any = {};
  private userProfileSettingChange$ = new Subject<{ key: string; value: string; }>();
  private systemCurrencyCode: string;
  /** Enforce currency symbols display regardless of the locale, ie for en_GB both USD and AUD will be displayed as $**/
  private enforceCurrencySymbol: string;

  constructor(private legacyInitializer: LegacyInitializer,
              private i18nService: InternationalizationService,
              private utilService: UtilService,
              private papa: Papa) {
    (window as any).NG_SHARED_USER_SERVICE = this;

    const that = this;
    legacyInitializer.legacyLoaded.subscribe((loaded) => {
        if (loaded) {
          that.setInfo();
        }
    });
  }

  userProfileSetting$(key: string) {
    return this.userProfileSettingChange$.pipe(
      startWith({ key, value: this.getUserProfileProperty(key) }),
      filter(data => data.key === key),
      map(data => data.value),
    );
  }


  setLanguage(language: string) {
    this.language = language;
    this.languageElements = this.language.split("_");
  }

  getSofiID() {
    return (this.sofiUser as any).id;
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  getAfterSessionTimeOutUrl(): string {
    return this.afterSessionTimeOutUrl || this.getDefaultLoginPage();
  }

  isAdministrator(): boolean {
    return this.hasRole('administrator');
  }

  getID(): string {
    return this.id;
  }

  userName(): string {
    return this.getUserName();
  }

  getTimeZone(): string {
    return this.timeZone;
  }

  getFirstName(): string {
    return this.firstName;
  }

  getLastName(): string {
    return this.lastName;
  }

  getFullName(): string {
    let separator = this.firstName && this.lastName ? ' ' : '';
    return `${this.firstName || ''}${separator}${this.lastName || ''}`
  }

  getInitials(): string {
    const firstName = this.firstName;
    const lastName = this.lastName;
    const firstInitial = (firstName && firstName.length > 0) ? firstName.charAt(0) : "";
    const lastInitial = (lastName && lastName.length > 0) ? lastName.charAt(0) : "";
    return firstInitial.toUpperCase() + lastInitial.toUpperCase();
  }

  getUserName(): string {
    return this.theUserName;
  }

  getLanguage(): string {
    return this.language;
  }
  getLocales(): LocaleKeyValue[] {
    return this.locales;
  }

  getLanguages(): LocaleKeyValue[] {
    return this.languages;
  }

  isMultilingual(): boolean {
    return this.multilingual || this.hasRole("language_editor");
  }

  getLocaleCustomizations(): LocaleCustomization[] {
    return this.localeCustomizations;
  }

  getSystemDefaultCurrencyCode() {
    return this.systemCurrencyCode;
  }

  getEnforceCurrencySymbol(): string {
    return this.enforceCurrencySymbol;
  }

  /* SR-967: getPhoto is deprecated.  Use the path /loggedinuserphoto for rendering a user's photo as an image. */
  getPhoto(): any {
    return this.photo;
  }

  getLanguageCode(): string {
    return this.languageElements?.[0];
  }

  getLanguageRegion(): string {
    return this.languageElements?.[1];
  }

  getFirstWeekDay(): string {
    return String(this.i18nService.getFirstDayOfWeek());
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getShortDateFormat(): string {
    return this.i18nService.DateTimeFormatMoment.SHORT_DATE;
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getLongDateFormat(): string {
    return this.i18nService.DateTimeFormatMoment.LONG_DATE;
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getShortTimeFormat(): string {
    return this.adjustTimeFormatForAMPM(this.i18nService.DateTimeFormatMoment.SHORT_TIME);
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getLongTimeFormat(): string {
    return this.adjustTimeFormatForAMPM(this.i18nService.DateTimeFormatMoment.LONG_TIME);
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getShortDateFormatCLDR(): string {
    return this.i18nService.getShortDateFormatCLDR();
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getLongDateFormatCLDR(): string {
    return this.i18nService.getLongDateFormatCLDR();
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getShortTimeFormatCLDR(): string {
    return this.adjustTimeFormatForAMPM(this.i18nService.getShortTimeFormatCLDR());
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getLongTimeFormatCLDR(): string {
    return this.adjustTimeFormatForAMPM(this.i18nService.getLongTimeFormatCLDR());
  }

  isAMPMTime(): boolean {
    return this.i18nService.isAMPMTime();
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getDigitsAfterDecimal(): string | number {
    return this.i18nService.getDigitsAfterDecimal();
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getDecimalSymbol(): string {
    return '.';
  }

  async impersonate(id: string) {
    const requestService = (window as any).NG_SHARED_REQUEST_SERVICE;
    const request: any = await requestService.createRequest()
        .controller("servicely.core.User")
        .method("impersonateUser")
        .data({ id: id })
        .execute();
    setTimeout(() => (window as any).location.reload(), 250);
    return request;
  }

  async unImpersonate() {
    const requestService = (window as any).NG_SHARED_REQUEST_SERVICE;
    const request: any = await requestService.createRequest()
        .controller("servicely.core.User")
        .method("unImpersonateUser")
        .execute();
    setTimeout(() => (window as any).location.reload(), 250);
    return request;
  }

  /**
   * @deprecated all formatting is done via the CLDR locale
   */
  getDigitGroupingSymbol(): string {
    // TODO
    return ',';
  }

  getRoles(): string[] {
    return this.roles;
  }

  hasRole(roleName: string): boolean {
    return this.roles.includes(roleName);
  }

  getHome(): string {
    return this.home;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  getBuildInfo(): string {
    return this.build;
  }

  getDeployedVersion(): string {
    return this.build.deployedVersion;
  }

  getProperty(key: string, defaultValue?: any): string {
    return this.properties[key] !== undefined ? this.properties[key] : defaultValue;
  }

  getBooleanProperty(key: string, defaultValue?: boolean): boolean {
    const value: string = this.getProperty(key);
    return value !== undefined ? this.utilService.toBoolean(value) : defaultValue;
  }

  getIntegerProperty(key: string, defaultValue?: number): number {
    const value: string = this.getProperty(key);
    return value !== undefined ? this.utilService.toInteger(value) : defaultValue;
  }

  public getUserProfileProperty(key: string, defaultValue?: string): string {
    return this.userProfile[key] === undefined ? defaultValue : this.userProfile[key];
  };

  public getIntegerUserProfileProperty(key: string, defaultValue?: number): number {
    return this.userProfile[key] === undefined ? defaultValue : parseInt(this.userProfile[key], 10);
  };

  public getBooleanUserProfileProperty(key: string, defaultValue?: boolean): boolean {
    return this.userProfile[key] === undefined ? defaultValue : (this.utilService.toBoolean(this.userProfile[key]) == true);
  };

  public parseCommaSeparatedProperty(value: string): string[] {
    const data = this.papa.parse(value).data as string[][];
    return data.length > 0 ? data[0].map((s) => s.trimEnd().trimStart()) : [];
  }

  public parseSpaceSeparatedProperty(value: string): string[] {
    return (value || "").split(/ +/g).filter(s => s.length > 0);
  }

  public async updateUserProfileSetting(key: string, value: string) {
    let result = "";

    try {

      // Store locally
      this.userProfile[key] = value;

      // Send to server
      // NOTE: Done this way via the property on 'window' to break a circular dependency.
      const requestService = (window as any).NG_SHARED_REQUEST_SERVICE;
      const response: any = await requestService.createRequest()
        .controller("servicely.core.User")
        .method("updateUserProfileSetting")
        .data({key, value})
        .execute();

      const responseData: any = response.data;

      if (responseData) {
        result = JSON.stringify(responseData);
      }
    } catch (e) {
      console.error("Exception updating user profile: ", e);
      result = "error";
    }

    this.userProfileSettingChange$.next({ key, value: this.userProfile[key] });

    return result;
  }

  public async deleteUserProfileSetting(key: string) {
    let result = "";

    try {

      // Delete locally
      if (this.userProfile[key]) {
        delete this.userProfile[key];
      }

      // Send to server
      // NOTE: Done this way via the property on 'window' to break a circular dependency.
      const requestService = (window as any).NG_SHARED_REQUEST_SERVICE;
      const response: any = await requestService.createRequest()
        .controller("servicely.core.User")
        .method("deleteUserProfileSetting")
        .data({key})
        .execute();

      const responseData: any = response.data;

      if (responseData) {
        result = JSON.stringify(responseData);
      }
    } catch (e) {
      console.error("Exception updating user profile: ", e);
      result = "error";
    }

    this.userProfileSettingChange$.next({ key, value: this.userProfile[key] });

    return result;
  }

  private adjustTimeFormatForAMPM(value: string) {
    if (value) {
      return this.isAMPMTime()
        ? value.replace(/H/g, "h")
        : value.replace(/h/g, "H");
    } else {
      return value;
    }
  }

  private setInfo() {
    if ((window as any).KO_USER_BRIDGE && (window as any).KO_USER_BRIDGE.userTableRecord) {
      this.setUserInfo((window as any).KO_USER_BRIDGE.userTableRecord,
        (window as any).KO_USER_BRIDGE.systemDefaults,
        (window as any).KO_USER_BRIDGE.properties);
      this.setSofiInfo((window as any).KO_USER_BRIDGE.sofiUser);
      this.setUserProfile((window as any).KO_USER_BRIDGE.userProfile);
      this.setHome((window as any).KO_USER_BRIDGE.homeURL);
      this.setBaseUrl((window as any).KO_USER_BRIDGE.baseUrl);
      this.setBuildInfo((window as any).KO_USER_BRIDGE.build);
      this.setAfterSessionTimeOutUrl((window as any).KO_USER_BRIDGE.afterSessionTimeOutUrl);
    }
  }

  private setUserInfo(userTableRecord: any, systemDefaults: any, properties: any) {
    this.authenticated = true;
    this.id = userTableRecord.id;
    this.theUserName = userTableRecord.UserName;
    this.firstName = userTableRecord.FirstName;
    this.lastName = userTableRecord.LastName;
    this.photo = UtilService.dot(userTableRecord, "Photo");
    this.timeZone = (userTableRecord.TimeZone === "none" || userTableRecord.TimeZone == null) ? systemDefaults.timeZone : userTableRecord.TimeZone;
    this.language = UtilService.dot(userTableRecord, "PreferredLanguage") || systemDefaults.language;
    this.locales = systemDefaults.locales;
    this.languages = systemDefaults.languages;
    this.multilingual = systemDefaults.multilingual;
    this.localeCustomizations = systemDefaults.localeCustomizations;
    this.languageElements = this.language.split("_");
    this.systemCurrencyCode = systemDefaults.currencyCode;
    this.enforceCurrencySymbol = systemDefaults.enforceCurrencySymbol;

    this.roles = userTableRecord.roles || [];

    this.properties = properties || {};
  }

  private setSofiInfo(sofiUser: any) {
    this.sofiUser = sofiUser || {};
  }

  private setBuildInfo(build: any) {
    this.build = build || {};
  }

  private setAfterSessionTimeOutUrl(afterSessionTimeOutUrl: string) {
    this.afterSessionTimeOutUrl = afterSessionTimeOutUrl || this.getDefaultLoginPage();
  }

  private setUserProfile(userProfile: any) {
    this.userProfile = userProfile || {};
  }

  private setHome(homeURL: string) {
    this.home = homeURL;
  }

  private setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getDefaultLoginPage() {
    return '/login#/';
  }
}
